import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ArrowLeft, Save, HandHeart, Upload, CheckCircle, AlertTriangle } from 'lucide-react'
import { LoadingInline } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import { logger } from '@/lib/logger'
import { cn } from '@/lib/utils'

interface ContribuicaoFormData {
  tipo: 'dizimo' | 'oferta' | 'missoes' | 'doacoes' | ''
  valor: string
  comprovante?: File | null
}

const TIPOS_CONTRIBUICAO = [
  { value: 'dizimo', label: 'Dízimo' },
  { value: 'oferta', label: 'Oferta' },
  { value: 'missoes', label: 'Missões' },
  { value: 'doacoes', label: 'Doações' },
]

export function ContribuicaoPage() {
  const navigate = useNavigate()
  const { perfil } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [formData, setFormData] = useState<ContribuicaoFormData>({
    tipo: '',
    valor: '',
    comprovante: null,
  })

  const handleInputChange = (field: keyof ContribuicaoFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    setFormData(prev => ({ ...prev, comprovante: file }))
  }

  const formatCurrency = (value: string) => {
    // Remove tudo que não é dígito
    const numericValue = value.replace(/\D/g, '')
    
    // Converte para número e formata
    const number = parseFloat(numericValue) / 100
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(number)
  }

  const handleValueChange = (value: string) => {
    const formatted = formatCurrency(value)
    handleInputChange('valor', formatted)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.tipo || !formData.valor) {
      return
    }

    setIsSubmitting(true)

    try {
      // Simular envio da contribuição
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      logger.info('Nova contribuição registrada')

      setShowSuccess(true)
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          tipo: '',
          valor: '',
          comprovante: null,
        })
        setShowSuccess(false)
      }, 3000)

    } catch (error) {
      logger.error('Erro ao registrar contribuição')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isFormValid = formData.tipo && formData.valor

  return (
    <div className="space-y-6 -mx-6">
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 sm:gap-4"
        >
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/')}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Contribuição</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Registre sua contribuição para a igreja
            </p>
          </div>
        </motion.div>
      </div>

      {/* Success Alert */}
      <div className="px-4 sm:px-6 lg:px-8">
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Contribuição registrada com sucesso! Obrigado pela sua generosidade.
              </AlertDescription>
            </Alert>
          </motion.div>
        )}
      </div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mx-6"
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HandHeart className="h-5 w-5" />
              Registrar Contribuição
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-4 sm:gap-6 sm:grid-cols-2">
                {/* Nome do Membro (não editável) */}
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="nome">Nome do Membro</Label>
                  <Input
                    id="nome"
                    value={perfil?.nome || ''}
                    disabled
                    className="bg-muted"
                  />
                </div>

                {/* Tipo de Contribuição */}
                <div className="space-y-3 sm:col-span-2">
                  <Label>Tipo de Contribuição *</Label>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {TIPOS_CONTRIBUICAO.map((tipo) => (
                      <Button
                        key={tipo.value}
                        type="button"
                        variant={formData.tipo === tipo.value ? "default" : "outline"}
                        className={cn(
                          "h-auto flex-col gap-2 p-4 text-center transition-all",
                          formData.tipo === tipo.value 
                            ? "bg-primary text-primary-foreground shadow-md" 
                            : "hover:bg-muted"
                        )}
                        onClick={() => handleInputChange('tipo', tipo.value)}
                      >
                        <HandHeart className="h-5 w-5" />
                        <span className="text-sm font-medium">{tipo.label}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Valor */}
                <div className="space-y-2">
                  <Label htmlFor="valor">Valor *</Label>
                  <Input
                    id="valor"
                    placeholder="R$ 0,00"
                    value={formData.valor}
                    onChange={(e) => handleValueChange(e.target.value)}
                  />
                </div>

                {/* Comprovante (opcional) */}
                <div className="space-y-2">
                  <Label>Comprovante (opcional)</Label>
                  <div className="space-y-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4" />
                      {formData.comprovante ? 'Alterar arquivo' : 'Escolher arquivo'}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    {formData.comprovante && (
                      <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                        Arquivo selecionado: {formData.comprovante.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full sm:w-auto"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className="shadow-md shadow-primary/20 w-full sm:w-auto"
                >
                  {isSubmitting ? (
                    <>
                      <LoadingInline />
                      <span className="hidden sm:inline">Registrando...</span>
                      <span className="sm:hidden">Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      <span className="hidden sm:inline">Registrar Contribuição</span>
                      <span className="sm:hidden">Registrar</span>
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>

      {/* Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-start gap-2 sm:gap-3">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="text-xs sm:text-sm font-medium text-blue-800 dark:text-blue-200">
                  Informações importantes
                </p>
                <ul className="text-xs sm:text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>• O comprovante é opcional, mas recomendado</li>
                  <li>• Todas as contribuições são registradas com segurança</li>
                  <li>• Dúvidas? Entre em contato com a tesouraria</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}